'use client';

import { Badge } from '@/components/badge';
import { DiscountBadge } from '@/components/discount-badge';
import { Form } from '@/components/pages/product-details/product-details-form';
import { WishlistButton } from '@/components/wishlist-button-product';
import { useProductInventory } from '@veranda/shared/cart/hooks';
import { BadgeOverlayContainer } from '@veranda/shared/components/badge-overlay-container';
import type { GalleryItem } from '@veranda/shared/components/lightbox/types';
import { ProductDetailsHero } from '@veranda/shared/components/pages/product-details';
import type { ProductPageProps } from '@veranda/shared/components/pages/product-details/client';
import Image from 'next/image';

export function Hero({ product }: { product: ProductPageProps }) {
  const { isOutOfStock, isLoading } = useProductInventory(product.productGid);

  const formattedGallery = [
    ...(product.mainImage
      ? [
          {
            type: 'image',
            ...product.mainImage,
          } as const,
        ]
      : []),
    ...(product.gallery || []),
  ].filter((item) => item !== null);

  const firstVariant = product.variants?.[0];

  return (
    <ProductDetailsHero>
      <ProductDetailsHero.GalleryContainer>
        <ProductDetailsHero.GalleryDesktop
          gallery={formattedGallery as unknown as GalleryItem[]}
          firstImageOverlay={
            <>
              <BadgeOverlayContainer>
                {!isLoading && isOutOfStock && <Badge>Sold out</Badge>}
                {product.defaultSettings?.showDiscountBadges && (
                  <DiscountBadge
                    price={firstVariant?.price ?? 0}
                    compareAtPrice={firstVariant?.compareAtPrice ?? 0}
                  />
                )}
              </BadgeOverlayContainer>
              {product.productGid &&
                firstVariant?.gid &&
                formattedGallery.length === 1 && (
                  <WishlistButton
                    productGid={product.productGid}
                    variantGid={firstVariant.gid}
                    className="absolute top-4 right-4 z-10 max-lg:right-auto max-lg:left-4 lg:right-4"
                  />
                )}
            </>
          }
          secondImageOverlay={
            product.productGid &&
            firstVariant?.gid && (
              <WishlistButton
                productGid={product.productGid}
                variantGid={firstVariant.gid}
                className="absolute top-4 right-4 z-10 max-lg:right-auto max-lg:left-4 lg:right-4"
              />
            )
          }
          overlay={
            product.brand?.name === 'Yabby' && (
              <Image
                src="/images/yabby-logo.png"
                width={100}
                height={100}
                alt="Yabby logo"
                className="absolute bottom-2 left-2"
              />
            )
          }
        />
        <ProductDetailsHero.GalleryMobile
          gallery={formattedGallery as unknown as GalleryItem[]}
          firstImageOverlay={
            <>
              <BadgeOverlayContainer>
                {!isLoading && isOutOfStock && <Badge>Sold out</Badge>}
                {product.defaultSettings?.showDiscountBadges && (
                  <DiscountBadge
                    price={firstVariant?.price ?? 0}
                    compareAtPrice={firstVariant?.compareAtPrice ?? 0}
                  />
                )}
              </BadgeOverlayContainer>
              {product.productGid && firstVariant?.gid && (
                <WishlistButton
                  productGid={product.productGid}
                  variantGid={firstVariant.gid}
                  className="absolute top-4 left-4 z-10"
                />
              )}
            </>
          }
        />
      </ProductDetailsHero.GalleryContainer>
      <Form product={product} />
    </ProductDetailsHero>
  );
}
